import * as React from 'react';
import { get, set } from 'lodash-es';
import { IVector3 } from '@easytwin/runtime';
import { toScaledVec3 } from '@/utils';
import { FormItemsInRow } from './FormItemsInRow';
import { LockIcon } from './LockIcon';
import { AnimationProInputNumber, AnimationProInputNumberProps } from './AnimationProInputNumber';

interface Vector3FormItemProps
  extends Omit<AnimationProInputNumberProps, 'onChange' | 'value' | 'onBlur' | 'prefix'> {
  label: string;
  value: IVector3;
  prefix?: [string, string, string];
  constrainable?: boolean;
  onChange?: (name: string, value: IVector3 | undefined) => void;
  onBlur: (name: string, value: IVector3 | undefined) => void;
  size?: 'large' | 'default' | 'small' | 'mini';
  isEuler?: boolean;
}

export const AnimationVector3FormItem: React.FC<Vector3FormItemProps> = (props) => {
  const {
    label,
    value,
    constrainable,
    name,
    prefix,
    onBlur,
    onChange,
    size = 'large',
    isEuler = false,
    realToInputValue,
    inputToRealValue,
    ...rest
  } = props;
  const [isScaled, setIsScaled] = React.useState(false);
  const handleChange = (n: string, v: number) => {
    const newValue = { ...value };
    set(newValue, n, v);
    const scaledValue = isScaled ? toScaledVec3(newValue, value) : newValue;
    onChange?.(name, scaledValue);
  };

  const handleBlur = (n: string, v: number) => {
    const newValue = { ...value };
    set(newValue, n, v);
    const scaledValue = isScaled ? toScaledVec3(newValue, value) : newValue;
    onBlur(name, scaledValue);
  };

  return (
    <div className='flex-1 flex items-center gap-x-2'>
      {constrainable && <LockIcon size={14} value={isScaled} onChange={setIsScaled} />}
      <FormItemsInRow>
        <AnimationProInputNumber
          name='x'
          value={value.x}
          isEuler={isEuler}
          prefix={prefix?.[0] || 'X'}
          {...rest}
          onChange={handleChange}
          onBlur={handleBlur}
          realToInputValue={
            isEuler
              ? (num: number) => {
                  return (num * 180) / Math.PI;
                }
              : realToInputValue
          }
          inputToRealValue={
            isEuler
              ? (num: number) => {
                  return (num / 180) * Math.PI;
                }
              : inputToRealValue
          }
        />
        <AnimationProInputNumber
          name='y'
          value={value.y}
          isEuler={isEuler}
          prefix={prefix?.[1] || 'Y'}
          {...rest}
          onChange={handleChange}
          onBlur={handleBlur}
          realToInputValue={
            isEuler
              ? (num: number) => {
                  return (num * 180) / Math.PI;
                }
              : realToInputValue
          }
          inputToRealValue={
            isEuler
              ? (num: number) => {
                  return (num / 180) * Math.PI;
                }
              : inputToRealValue
          }
        />
        <AnimationProInputNumber
          name='z'
          value={value.z}
          isEuler={isEuler}
          prefix={prefix?.[2] || 'Z'}
          {...rest}
          onChange={handleChange}
          onBlur={handleBlur}
          realToInputValue={
            isEuler
              ? (num: number) => {
                  return (num * 180) / Math.PI;
                }
              : realToInputValue
          }
          inputToRealValue={
            isEuler
              ? (num: number) => {
                  return (num / 180) * Math.PI;
                }
              : inputToRealValue
          }
        />
      </FormItemsInRow>
    </div>
  );
};
